import * as React from 'react';
import { CalendarOff } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@ooh/ui/components/table';
import { cn } from '@ooh/ui/lib/utils';
import { STICKY_COL_SHADOW, UnitAuditTimeline } from './unit-reservations-tab-parts';
import { UnitReservationTableRow } from './unit-reservation-table-row';
import type { UnitReservationsTabProps } from './unit-reservations-tab-types';

export type {
  UnitAuditEntry,
  UnitQueueLabels,
  UnitReservationRow,
  UnitReservationsPermissions,
  UnitReservationsTabLabels,
  UnitReservationsTabProps,
} from './unit-reservations-tab-types';

// ─── Component ───────────────────────────────────────────────

export function UnitReservationsTab({
  reservations,
  auditEntries,
  focusReservationId,
  permissions,
  labels,
  currentUserId,
  onAction,
  onBlockForMaintenance,
  canBlockForMaintenance = false,
  queueByReservationId,
  queueLabels,
  onQueueAction,
  onOpenFullQueue,
  openFullQueueLabel,
}: UnitReservationsTabProps) {
  const [expandedQueues, setExpandedQueues] = React.useState<ReadonlySet<number>>(new Set());
  const focusRowRef = React.useRef<HTMLTableRowElement | null>(null);

  const toggleQueue = (reservationId: number) => {
    setExpandedQueues((prev) => {
      const next = new Set(prev);
      if (next.has(reservationId)) next.delete(reservationId);
      else next.add(reservationId);
      return next;
    });
  };

  // ─── 2-second focus highlight ─────────────────────────────
  // spec.md:183: "highlight class for at least 2 seconds"
  const [isHighlighted, setIsHighlighted] = React.useState(focusReservationId !== null);

  React.useEffect(() => {
    if (!focusReservationId) return;
    setIsHighlighted(true);
    const timer = setTimeout(() => setIsHighlighted(false), 2000);
    return () => clearTimeout(timer);
  }, [focusReservationId]);

  React.useEffect(() => {
    if (focusRowRef.current) {
      focusRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusReservationId]);

  // Empty state
  if (reservations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <CalendarOff className="h-10 w-10 text-muted-foreground/40" />
        <div>
          <p className="text-[15px] font-medium text-foreground">{labels.emptyTitle}</p>
          <p className="text-[13px] text-muted-foreground mt-1 max-w-xs">
            {labels.emptyDescription}
          </p>
        </div>
        {canBlockForMaintenance ? (
          <Button variant="outline" size="sm" onClick={onBlockForMaintenance}>
            {labels.blockForMaintenanceLabel}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ─── Reservations table ──────────────────── */}
      <div
        data-testid="unit-reservations-table"
        className="overflow-x-auto rounded-lg border border-border bg-background"
      >
        <Table className="text-[13px] min-w-[760px]">
          <TableHeader className="sticky top-0 bg-background z-20">
            <TableRow className="border-b border-border">
              <TableHead className="w-[170px] min-w-[150px]">{labels.statusLabel}</TableHead>
              <TableHead className="w-[140px] min-w-[120px]">{labels.holderLabel}</TableHead>
              <TableHead className="w-[96px] min-w-[88px]">{labels.dateFromLabel}</TableHead>
              <TableHead className="w-[96px] min-w-[88px]">{labels.dateToLabel}</TableHead>
              {/* Expiry deadline — a separate column from the campaign window
                  (Od/Do): the campaign window states which dates the unit is
                  taken, the deadline states when the RESERVATION ITSELF
                  lapses and is released. Display-only, not sortable
                  (reservation-panel spec). */}
              <TableHead className="w-24 min-w-22" title={labels.deadlineTooltipLabel}>
                {labels.deadlineLabel}
              </TableHead>
              <TableHead className="w-[180px] min-w-[140px]">{labels.offerLabel}</TableHead>
              <TableHead className="w-[130px] min-w-[110px]">{labels.sourceLabel}</TableHead>
              <TableHead className="w-[64px] min-w-[56px] text-center">
                {labels.queuedLabel}
              </TableHead>
              <TableHead
                className={cn(
                  'sticky right-0 bg-background z-30 w-[48px] min-w-[48px] text-center',
                  STICKY_COL_SHADOW,
                )}
              >
                {labels.actionsLabel}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reservations.map((item, idx) => (
              <UnitReservationTableRow
                key={item.id}
                item={item}
                index={idx}
                permissions={permissions}
                labels={labels}
                currentUserId={currentUserId}
                isFocused={item.id === focusReservationId}
                isHighlighted={isHighlighted}
                focusRowRef={focusRowRef}
                queueEntries={queueByReservationId?.[item.id] ?? []}
                queueLabels={queueLabels}
                isQueueOpen={expandedQueues.has(item.id)}
                onToggleQueue={toggleQueue}
                onAction={onAction}
                onQueueAction={onQueueAction}
                onOpenFullQueue={onOpenFullQueue}
                openFullQueueLabel={openFullQueueLabel}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {/* ─── "Zablokuj dostępność" CTA — permission-gated on media_unit.block_availability ── */}
      {canBlockForMaintenance ? (
        <div className="flex justify-start">
          <Button variant="outline" size="sm" onClick={onBlockForMaintenance}>
            {labels.blockForMaintenanceLabel}
          </Button>
        </div>
      ) : null}

      <UnitAuditTimeline auditEntries={auditEntries} labels={labels} />
    </div>
  );
}
